import { useMutation } from '@tanstack/react-query'

import { guessTimezone } from '@/src/general/primitives/dates/dayjs'
import { Cookie } from '@/src/general/utils/cookies/Cookie'
import { getAppType } from '@/src/general/utils/getAppType'

import { notificationApi } from '../api/notificationApi'

const arrayBufferToString = (key: ArrayBuffer | null) =>
  key && btoa(String.fromCharCode.apply(null, [...new Uint8Array(key)]))

/**
 * Registers a device for push notifications.
 *
 * @returns a mutation function that takes a `PushSubscription` as an argument and
 * returns a promise that resolves to the newly created device's ID.
 *
 * @remarks
 * The `onSuccess` callback is used to store the device ID in a cookie
 *
 * @example
 * const { mutate } = usePostDeviceMutation()
 * mutate(pushSubscription)
 */
export const usePostDeviceMutation = () =>
  useMutation({
    mutationFn: (pushSubscription: PushSubscription) =>
      notificationApi.postDevice({
        endpoint: pushSubscription.endpoint,
        expirationTime: pushSubscription.expirationTime,
        keys: {
          p256dh: arrayBufferToString(pushSubscription.getKey('p256dh')),
          auth: arrayBufferToString(pushSubscription.getKey('auth')),
        },
        timezone: guessTimezone(),
        userAgent: window.navigator.userAgent,
        appType: getAppType(),
      }),
    onSuccess: ({ id }) => Cookie.set('deviceID', id, 'oneYear'),
  })
